import { ApiErrorBody, type ApiErrorCode } from '@grc/shared';

/**
 * What went wrong with a call, as a code the screens turn into words (errors.ts): one of the API's
 * refusals (`API_ERRORS` in @grc/shared), or one of the client's own: `network` when no answer
 * came (offline, or Caddy's 502 to 504 while the service restarts at a deploy), `unexpected` for
 * an answer that isn't the API's.
 */
export type ErrorCode = ApiErrorCode | 'network' | 'unexpected';

/** A failed API call: the HTTP status (0 when nothing answered), the code, and a lock's wait. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  /** With `locked`: the seconds until the next try. */
  readonly retryAfter: number | undefined;

  constructor(status: number, code: ErrorCode, retryAfter?: number) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

/** The refusals that mean the session itself changed: the session (session.tsx) acts on them. */
export type SessionSignal = 'unauthenticated' | 'disabled' | 'password_change_required';

function isSessionSignal(code: ErrorCode): code is SessionSignal {
  return code === 'unauthenticated' || code === 'disabled' || code === 'password_change_required';
}

let sessionListener: ((signal: SessionSignal) => void) | undefined;

/**
 * Tells `listener` of every refusal that changes the session, whichever screen made the call, so
 * a 401 anywhere sends the member to the login (D21, D22). One listener at a time: the session's.
 * Returns the way to stop.
 */
export function onSessionSignal(listener: (signal: SessionSignal) => void): () => void {
  sessionListener = listener;
  return () => {
    if (sessionListener === listener) sessionListener = undefined;
  };
}

// Caddy's own answers when the service doesn't: the same, for a member, as no answer at all.
const GATEWAY_ERRORS: ReadonlySet<number> = new Set([502, 503, 504]);

async function failure(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  const refusal = ApiErrorBody.safeParse(body);
  if (refusal.success) return new ApiError(res.status, refusal.data.error, refusal.data.retryAfter);
  return new ApiError(res.status, GATEWAY_ERRORS.has(res.status) ? 'network' : 'unexpected');
}

/**
 * Calls the API at the same origin (the dev server, the preview and Caddy all proxy /api) and
 * returns the JSON body. A body is sent as JSON; the session cookie travels by itself. Anything
 * but a success throws an ApiError.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  let res: Response;
  try {
    res = await fetch(path, { credentials: 'same-origin', ...init, headers });
  } catch {
    throw new ApiError(0, 'network');
  }
  if (!res.ok) {
    const error = await failure(res);
    if (isSessionSignal(error.code)) sessionListener?.(error.code);
    throw error;
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(res.status, 'unexpected');
  }
}

/** What reads an answer: a zod schema from @grc/shared. */
export type Schema<T> = {
  safeParse: (value: unknown) => { success: true; data: T } | { success: false };
};

/**
 * The answer of a call as its schema reads it (A5's way, for every screen's calls since B3);
 * anything else is `unexpected`.
 */
export async function readAs<T>(schema: Schema<T>, call: Promise<unknown>): Promise<T> {
  const answer = schema.safeParse(await call);
  if (!answer.success) throw new ApiError(200, 'unexpected');
  return answer.data;
}

/** `GET path`. */
export function getJson<T = unknown>(path: string): Promise<T> {
  return api<T>(path);
}

/** `POST path`, with `body` as JSON when there is one. */
export function postJson<T = unknown>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

/** `PATCH path`, with `body` as JSON. */
export function patchJson<T = unknown>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

/**
 * `DELETE path`, with `body` as JSON: the admin API's removal takes `{}`, so that only a page that
 * sends JSON, ours, can make it (D21, D23).
 */
export function deleteJson<T = unknown>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'DELETE', body: JSON.stringify(body) });
}
